/**
 * One-click starting points for the settings panel.
 *
 * Each preset is a partial patch over `DEFAULT_OPTIONS`, so adding a new option
 * later does not require touching every preset.
 */

import type { CleanOptions } from './types';

export interface Preset {
  id: string;
  label: string;
  hint: string;
  patch: Partial<CleanOptions>;
}

export const PRESETS: Preset[] = [
  {
    id: 'clean',
    label: 'Clean HTML',
    hint: 'Keep the structure, drop all styling and junk attributes. The everyday choice.',
    patch: {
      stripClassesIds: true,
      stripInlineStyles: true,
      stripDataAttrs: true,
      stripOtherAttrs: true,
      semanticTags: true,
      unwrapSpans: true,
      unwrapDivs: true,
      removeEmpty: true,
      allowedTags: '',
      stripLinks: false,
      stripImages: false,
      stripMedia: true,
      stripFormElements: true,
      stripTables: false,
      stripComments: true,
      convertNbsp: true,
      collapseWhitespace: true,
      normalizeQuotes: false,
      stripZeroWidth: true,
    },
  },
  {
    id: 'bare',
    label: 'Bare bones',
    hint: 'Headings, paragraphs, lists, bold and italic. Nothing else survives.',
    patch: {
      stripClassesIds: true,
      stripInlineStyles: true,
      stripDataAttrs: true,
      stripOtherAttrs: true,
      semanticTags: true,
      unwrapSpans: true,
      unwrapDivs: true,
      removeEmpty: true,
      allowedTags: 'h1 h2 h3 h4 h5 h6 p ul ol li strong em blockquote br code pre',
      stripLinks: true,
      stripImages: true,
      stripMedia: true,
      stripFormElements: true,
      stripTables: true,
      stripComments: true,
      convertNbsp: true,
      collapseWhitespace: true,
      normalizeQuotes: true,
      stripZeroWidth: true,
    },
  },
  {
    id: 'wordpress',
    label: 'For WordPress',
    hint: 'Clean structure, links and images kept, straight quotes, tidy indenting.',
    patch: {
      stripClassesIds: true,
      stripInlineStyles: true,
      stripDataAttrs: true,
      stripOtherAttrs: true,
      semanticTags: true,
      unwrapSpans: true,
      unwrapDivs: true,
      removeEmpty: true,
      allowedTags: '',
      stripLinks: false,
      stripImages: false,
      stripMedia: true,
      stripFormElements: true,
      stripTables: false,
      stripComments: true,
      convertNbsp: true,
      collapseWhitespace: true,
      normalizeQuotes: true,
      stripZeroWidth: true,
      tidy: true,
    },
  },
  {
    id: 'light',
    label: 'Light touch',
    hint: 'Only remove styling and invisible characters — leave everything else alone.',
    patch: {
      stripClassesIds: false,
      stripInlineStyles: true,
      stripDataAttrs: false,
      stripOtherAttrs: false,
      semanticTags: false,
      unwrapSpans: false,
      unwrapDivs: false,
      removeEmpty: false,
      allowedTags: '',
      stripLinks: false,
      stripImages: false,
      stripMedia: false,
      stripFormElements: false,
      stripTables: false,
      stripComments: true,
      convertNbsp: true,
      collapseWhitespace: false,
      normalizeQuotes: false,
      stripZeroWidth: true,
    },
  },
];
